import { ComponentType, useContext, useState } from 'react';
import {
  Window,
  WindowActionsBar,
  WindowProps
} from '@progress/kendo-react-dialogs';
import { FluidForm } from '../../../components/forms';
import { ZoneEditor } from './zone-editor';
import { ResourceEntity, ZoneEntity } from '../../../types/game-document/';
import {
  CheckboxChangeEvent,
  InputChangeEvent
} from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import { EditorMode } from '../../../types/editor-mode';
import { EntityEditor } from '../../../types/game-document/entity-editor';
import { GameDocumentContext } from '../../../contexts/game-document';

interface ZoneEditorWindowProps extends WindowProps {
  editorEntity: EntityEditor<ZoneEntity>;
  editorResource: EntityEditor<ResourceEntity>[];
  toggleDialog: Function;
  onSubmit: (
    editorEntity: EntityEditor<ZoneEntity>,
    resourceEntity: EntityEditor<ResourceEntity>[]
  ) => void;
  editorMode: EditorMode;
}

export interface RequiredInput {
  name: string;
  errorMessage: string;
}

export const ZoneEditorWindow: ComponentType<ZoneEditorWindowProps> = ({
  editorEntity,
  toggleDialog,
  editorResource,
  onSubmit,
  editorMode,
  ...props
}: ZoneEditorWindowProps) => {
  const [state, setState] = useContext(GameDocumentContext);
  const [entity, setEntity] = useState({ ...editorEntity.entity });
  const [resources, setResources] = useState<ResourceEntity[]>([
    ...editorResource.map((title) => title.entity)
  ]);

  const [requiredInputs, setRequiredInputs] = useState<RequiredInput[]>([
    { name: 'titleResId', errorMessage: '' }
  ]);

  const handleInputChange = (event: InputChangeEvent | CheckboxChangeEvent) => {
    if (event.target.name === 'titleResId') {
      const { name, value } = event.target;
      setResources(
        resources.map((item) =>
          item.id === entity![name]
            ? { ...item, value: value! as string }
            : { ...item }
        )
      );
    } else {
      setEntity({ ...entity, [event.target.name!]: event.target.value });
    }
  };

  const isInputValid = (): boolean => {
    const inputValidation = requiredInputs.map((input) => {
      const titleRes = resources.find(
        (r) => r.id === editorEntity.entity.titleResId
      );

      input.errorMessage = '';
      if (input.name === 'titleResId' && titleRes !== undefined) {
        // Check validation for input title

        if (titleRes.value === '') {
          input.errorMessage = 'This field is required';
        }
      }

      return input;
    });
    setRequiredInputs(inputValidation);
    return (
      inputValidation.findIndex((input) => input.errorMessage !== '') === -1
    );
  };

  return (
    <>
      <Window
        modal={true}
        minimizeButton={undefined}
        className={'shadow'}
        initialWidth={500}
        initialHeight={375}
        onClose={() => toggleDialog()}
        title={editorEntity.isNew ? 'New zone map' : editorEntity.entity.name}
        {...props}>
        <FluidForm>
          <ZoneEditor
            editorMode={editorMode}
            entity={entity}
            resources={resources}
            handleInputChange={handleInputChange}
            requiredInputs={requiredInputs}
          />
        </FluidForm>
        <WindowActionsBar>
          <Button themeColor={'secondary'} onClick={() => toggleDialog()}>
            Cancel
          </Button>
          <Button
            themeColor={'primary'}
            onClick={() => {
              if (isInputValid()) {
                const titleSetting =
                  state.gameDocument?.settings.designer
                    .defaultZoneTitleVisibility ?? false;
                const entityResources = resources.map((item, index) => ({
                  isNew: editorResource[index].isNew,
                  entity: item
                })) as EntityEditor<ResourceEntity>[];
                onSubmit(
                  {
                    isNew: editorEntity.isNew,
                    entity: { ...entity, isTitleVisible: titleSetting }
                  },
                  entityResources
                );
              }
            }}>
            Save
          </Button>
        </WindowActionsBar>
      </Window>
    </>
  );
};
